import { useEffect, useRef, useState } from "react";
import { Flame, Sparkles, Loader2, X } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

/**
 * StarterPackModal — the single highest ARPPU lever in the monetization playbook.
 * Fires ONCE per user, on their first committed death (F3+ or 5+ kills), during D0/D1
 * of their account life. 15-minute countdown. If they dismiss or the timer expires,
 * it never shows again.
 *
 * See /app/MONETIZATION_STRATEGY.md §4.2 / §5.2.
 */
export default function StarterPackModal({ open, floor, kills, onClose, onPurchased }) {
  const [pack, setPack] = useState(null);          // packaging (price, weapon name, perks)
  const [status, setStatus] = useState(null);      // {eligible, seconds_remaining, offered_at}
  const [remaining, setRemaining] = useState(0);
  const [busy, setBusy] = useState(false);
  const tickRef = useRef(null);
  const initedRef = useRef(false);

  // On open: fetch products for the pack packaging AND either fetch status or mark-offered.
  useEffect(() => {
    if (!open || initedRef.current) return;
    initedRef.current = true;
    (async () => {
      try {
        const [prods, offer] = await Promise.all([
          api.get("/store/products"),
          api.post("/store/starter-pack/mark-offered", { floor: floor || 0, kills: kills || 0 }),
        ]);
        setPack(prods.starter_pack);
        setStatus(offer);
        setRemaining(Math.max(0, offer.seconds_remaining || 0));
      } catch (e) {
        // If mark-offered 409'd (already dismissed/purchased) or 400'd, just don't render.
        const detail = e?.response?.data?.detail || "";
        if (!/eligible|committed/i.test(detail)) {
          toast.error(detail || "Could not load the Starter Pack.");
        }
        onClose?.();
      }
    })();
  }, [open, floor, kills, onClose]);

  // Countdown ticker
  useEffect(() => {
    if (!open || remaining <= 0) return;
    tickRef.current = setInterval(() => {
      setRemaining((r) => {
        if (r <= 1) {
          clearInterval(tickRef.current);
          toast.info("The sponsor's offer has faded.");
          onClose?.();
          return 0;
        }
        return r - 1;
      });
    }, 1000);
    return () => clearInterval(tickRef.current);
  }, [open, remaining > 0, onClose]);   // eslint-disable-line react-hooks/exhaustive-deps

  if (!open || !pack || !status?.eligible) return null;

  const mm = String(Math.floor(remaining / 60)).padStart(2, "0");
  const ss = String(remaining % 60).padStart(2, "0");
  const price = (pack.price / 100).toFixed(2);

  const claim = async () => {
    setBusy(true);
    try {
      const res = await api.post("/store/checkout", {
        product_key: "pack_starter",
        origin_url: window.location.origin,
      });
      if (res?.url) {
        onPurchased?.();
        window.location.href = res.url;
      } else {
        throw new Error("No checkout URL");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "The merchant refused the trade.");
      setBusy(false);
    }
  };

  const dismiss = async () => {
    setBusy(true);
    try {
      await api.post("/store/starter-pack/dismiss", {});
      toast.info("Starter Pack dismissed. It won't return.");
    } catch {
      /* dismissal is fire-and-forget from the UX perspective */
    } finally {
      onClose?.();
    }
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center px-4"
      data-testid="starter-pack-modal"
      style={{ background: "rgba(0,0,0,0.88)", backdropFilter: "blur(8px)" }}
    >
      <div
        className="panel p-8 max-w-2xl w-full relative overflow-hidden"
        style={{
          background: "radial-gradient(ellipse at top, rgba(80,40,10,0.55) 0%, rgba(10,5,3,0.98) 60%)",
          borderColor: "rgba(212,175,55,0.55)",
          boxShadow: "0 0 80px rgba(212,175,55,0.30)",
        }}
      >
        {/* Close (dismisses permanently) */}
        <button
          onClick={dismiss}
          disabled={busy}
          className="absolute top-3 right-3 text-[#8b7965] hover:text-[#f0e6d2] transition-colors disabled:opacity-40"
          data-testid="starter-pack-close-btn"
          aria-label="Dismiss forever"
        >
          <X size={18} />
        </button>

        {/* Header */}
        <div className="text-center">
          <div className="inline-flex items-center gap-2 text-[10px] tracking-[0.5em] gold-text mb-3">
            <Sparkles size={12} /> ONE-TIME OFFER <Sparkles size={12} />
          </div>
          <h2 className="font-heading text-4xl md:text-5xl text-[#f0e6d2]">
            The <span className="gold-text italic">Starter</span> Pact
          </h2>
          <p className="font-narrative text-base md:text-lg ash-text italic mt-4 max-w-md mx-auto">
            The Whispering Ember offers a single pact — enough to give the next descent
            real weight. This offer will not return.
          </p>
        </div>

        {/* Countdown */}
        <div
          className="mt-6 mx-auto max-w-xs text-center panel py-3"
          data-testid="starter-pack-countdown"
          style={{
            background: "linear-gradient(180deg, rgba(70,30,10,0.4), rgba(20,10,5,0.85))",
            borderColor: remaining < 60 ? "rgba(226,88,34,0.55)" : "rgba(212,175,55,0.35)",
          }}
        >
          <div className="text-[9px] tracking-[0.4em] ash-text">OFFER EXPIRES IN</div>
          <div className="font-heading text-4xl mt-1" style={{ color: remaining < 60 ? "#e25822" : "#d4af37" }}>
            {mm}:{ss}
          </div>
        </div>

        {/* Contents */}
        <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="panel p-4 text-center" style={{ background: "linear-gradient(180deg,rgba(30,20,8,0.6),rgba(8,5,3,0.9))" }}>
            <div className="font-heading text-4xl gold-text">{pack.lives}</div>
            <div className="text-[10px] tracking-[0.3em] ash-text mt-1">BONUS EMBERS</div>
            <div className="text-[9px] ash-text mt-1 italic">Immediate</div>
          </div>
          <div className="panel p-4 text-center" style={{ background: "linear-gradient(180deg,rgba(30,20,8,0.6),rgba(8,5,3,0.9))" }}>
            <div className="font-heading text-4xl gold-text">{pack.gold}</div>
            <div className="text-[10px] tracking-[0.3em] ash-text mt-1">STARTER GOLD</div>
            <div className="text-[9px] ash-text mt-1 italic">Next run</div>
          </div>
          <div className="panel p-4 text-center relative overflow-hidden" style={{ background: "linear-gradient(180deg,rgba(50,20,50,0.55),rgba(10,5,10,0.9))", borderColor: "rgba(180,100,220,0.4)" }}>
            <div className="text-[9px] tracking-[0.3em] mt-1" style={{ color: "#c98cff" }}>
              {pack.weapon_tier?.toUpperCase() || "RARE"}
            </div>
            <div className="font-heading text-lg mt-1" style={{ color: "#f0e6d2" }}>{pack.weapon_name}</div>
            <div className="text-[9px] ash-text mt-1 italic">Next run</div>
          </div>
        </div>

        {/* CTA */}
        <button
          onClick={claim}
          disabled={busy}
          data-testid="starter-pack-claim-btn"
          className="btn-primary mt-6 w-full text-lg py-3 flex items-center justify-center gap-3 disabled:opacity-50 disabled:cursor-wait"
          style={{ borderColor: "#d4af37", background: "linear-gradient(180deg, #3a2410, #1a0f05)", color: "#f6e8b8" }}
        >
          {busy ? <><Loader2 size={16} className="animate-spin" /> REDIRECTING TO STRIPE...</> : <><Flame size={16} /> CLAIM STARTER PACK · ${price}</>}
        </button>

        <button
          onClick={dismiss}
          disabled={busy}
          data-testid="starter-pack-dismiss-btn"
          className="mt-3 w-full text-[10px] tracking-[0.4em] ash-text hover:text-[#f0e6d2] transition-colors disabled:opacity-40"
        >
          NOT NOW — LET IT FADE
        </button>

        <div className="mt-4 text-center text-[9px] tracking-[0.3em] ash-text">
          SECURE PAYMENT · STRIPE · ONE-TIME · NON-REFUNDABLE
        </div>
      </div>
    </div>
  );
}
